import useInfiniteAiSuggestions from 'Hooks/useInfiniteAiSuggestions';
import AISuggestionsDrawerContactDetected from './AISuggestionsDrawerContactDetected';
import Loading from 'Components/Atoms/Loading/Loading';
import { useTranslation } from 'react-i18next';
import { CURRENT_PLANS } from 'Utils/constants';
import useProjectSelected from 'Hooks/useProjectSelected';
import Button from 'Components/Atoms/Button/Button';
import usePlansAndPricingDialog from 'Hooks/usePlansAndPricingDialog';
import PropTypes from 'prop-types';
import { trackError } from 'Utils/errorMonitoring';
import InfinitePagination from 'Components/Molecules/InfinitePagination/InfinitePagination';
import AISuggestionsDrawerContactDuplicatedDetected from './AISuggestionsDrawerContactDuplicatedDetected';

const TYPES_COMPONENTS = {
    contact_detected: AISuggestionsDrawerContactDetected,
    contact_duplicated_detected: AISuggestionsDrawerContactDuplicatedDetected,
};

export default function AISuggestionsDrawer({ onClose }) {
    const { data, isLoading, acceptSuggestion, rejectSuggestion, isFetching, fetchNextPage, hasNextPage } =
        useInfiniteAiSuggestions();

    const { t } = useTranslation('common');
    const { project, isLoading: isProjectLoading } = useProjectSelected();
    const { openPlansAndPricing } = usePlansAndPricingDialog();

    const handleSeePlansAndPrices = () => {
        openPlansAndPricing();
        onClose?.();
    };

    const suggestions = data?.pages?.flatMap((page) => page.items) || [];

    const handleAcceptSuggestion = ({ suggestionId, data }) => {
        if (!data) return;

        const {
            id,
            type,
            name,
            categorization_keywords,
            categories_ids,
            tax_id,
            email,
            phone_number,
            address,
            city,
            state,
            postal_code,
            country,
        } = data;

        acceptSuggestion({
            suggestionId,
            data: {
                contact: {
                    id,
                    type,
                    name,
                    categorization_keywords,
                    categories_ids,
                    tax_id,
                    email,
                    phone_number,
                    address,
                    city,
                    state,
                    postal_code,
                    country,
                },
            },
        });
    };

    if (isLoading || isProjectLoading) {
        return (
            <div className="flex justify-center py-4">
                <Loading />
            </div>
        );
    }

    if (!CURRENT_PLANS.includes(project?.subscription?.plan)) {
        return (
            <div className="text-center py-4">
                <p className="text-gray-500 mb-4 text-pretty">{t('ai-suggestions-not-available-plan')}</p>
                <Button variant="primary" onClick={handleSeePlansAndPrices}>
                    {t('see-plans-and-prices')}
                </Button>
            </div>
        );
    }

    if (!suggestions.length) {
        return (
            <div className="text-center py-4">
                <p className="text-gray-500">{t('no-ai-suggestions')}</p>
            </div>
        );
    }

    return (
        <InfinitePagination hasNextPage={hasNextPage} fetchNextPage={fetchNextPage} isFetching={isFetching}>
            <div className="grid gap-2 pb-6 pt-2.5">
                {suggestions.map((suggestion) => {
                    const Component = TYPES_COMPONENTS[suggestion.type];
                    if (!Component) {
                        trackError(`Unknown suggestion type: ${suggestion.type}`);
                        return null;
                    }

                    return (
                        <Component
                            key={suggestion.id}
                            {...suggestion}
                            onAccept={(data) => handleAcceptSuggestion({ suggestionId: suggestion.id, data })}
                            onReject={() => rejectSuggestion({ suggestionId: suggestion.id })}
                        />
                    );
                })}
            </div>
        </InfinitePagination>
    );
}

AISuggestionsDrawer.propTypes = {
    onClose: PropTypes.func,
};
